import { CHECKLIST_TYPES, COMMUNICATION_EVENTS, STATUS_IDENTIFIERS } from "models";
import { StatusBarIcon } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/AppointmentStatus/AppointmentStatus.scss";
import { useAppointmentLocation, useRealTimeOrderedChecks } from "modules/AppointmentDetails/hooks";
import "modules/HoldingAppointmentDetails/components/AppointmentStatus/AppointmentStatus.scss";
import { isCarReady, isQualityCheck } from "utils/appointmentUtils";

import type { Appointment, CarCheck, CommunicationEvent, DealerLocation } from "models";

type AppointmentStatusProps = {
  appointment: Appointment;
};

type StatusIconProps = {
  status: STATUS_IDENTIFIERS;
  active?: boolean;
  name?: string;
  timestamp?: string | null;
  disabled?: boolean;
  color?: string;
  isMechanicState?: boolean;
  checkPausedAt?: string | null;
};

type QualityCheckOrCarReadyStatusProps = {
  appointment: Appointment;
  selectedLocation?: DealerLocation;
};

const getCustomerAnsweredName = (events?: CommunicationEvent[]) => {
  const answeredIndex = events?.findLastIndex(event => event.type === COMMUNICATION_EVENTS.DiagnoseOverviewAnswered) ?? -1;
  if (answeredIndex === -1) return "";

  const answeredEvent = events?.[answeredIndex];
  if (answeredEvent?.customer_name) return answeredEvent.customer_name;

  const customer = events?.findLast((event, index) => index <= answeredIndex && event.customer)?.customer;
  if (!customer) return "";

  return `${customer.firstname} ${customer.surname ?? ""}`.trim();
};

const getAppointmentStatusNameAndTime = (status: STATUS_IDENTIFIERS, appointment: Appointment | null) => {
  const statusHistory = appointment?.status_history?.findLast(history => status === history.identifier);
  if (!statusHistory) return { name: "", timestamp: "" };

  if (statusHistory.user_id === 0 && statusHistory.identifier === STATUS_IDENTIFIERS.CustomerAnsweredStatus)
    return { name: getCustomerAnsweredName(appointment?.customer_communication?.events), timestamp: statusHistory.created_on };

  return { name: statusHistory.last_user, timestamp: statusHistory.created_on };
};

const getIntermittentStatus = (appointment: Appointment, checks?: CarCheck[]) => {
  const { name, timestamp } = getAppointmentStatusNameAndTime(appointment.appointment_status_identifier, appointment);

  let statusIconProps: StatusIconProps = {
    name,
    timestamp,
    disabled: true,
    status: STATUS_IDENTIFIERS.CarCheckStatus
  };

  const noQualityChecklistOnAppointment = !checks?.some(check => check.checklist?.checklist_type === CHECKLIST_TYPES.Quality);

  switch (appointment.appointment_status_identifier) {
    case STATUS_IDENTIFIERS.NewCarStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.CarCheckStatus, name: "", timestamp: null, isMechanicState: true };
      break;
    case STATUS_IDENTIFIERS.CarCheckStartedStatus: {
      if (noQualityChecklistOnAppointment)
        statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.CarCheckStartedStatus, checkPausedAt: appointment.car_check_paused_at, active: true };

      break;
    }
    case STATUS_IDENTIFIERS.CarCheckStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.CarCheckStatus, active: true };
      break;
    case STATUS_IDENTIFIERS.AannameOKStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.AannameOKStatus, active: true };
      break;
    case STATUS_IDENTIFIERS.DiagnoseStatus:
      statusIconProps = { ...statusIconProps, status: STATUS_IDENTIFIERS.DiagnoseStatus, active: true };
      break;
    default: {
      const lastStatus = appointment.status_history?.findLast(
        status =>
          [STATUS_IDENTIFIERS.CarCheckStartedStatus, STATUS_IDENTIFIERS.CarCheckStatus, STATUS_IDENTIFIERS.AannameOKStatus, STATUS_IDENTIFIERS.DiagnoseStatus].includes(
            status.identifier
          ) && noQualityChecklistOnAppointment
      );
      if (lastStatus)
        statusIconProps = {
          ...statusIconProps,
          status: lastStatus.identifier,
          name: lastStatus.last_user,
          timestamp: lastStatus.created_on,
          isMechanicState: true,
          active: false
        };
      else {
        statusIconProps = {
          ...statusIconProps,
          name: "",
          timestamp: null
        };
      }
    }
  }

  return <StatusBarIcon {...statusIconProps} />;
};

const QualityCheckOrCarReadyStatus = ({ appointment, selectedLocation }: QualityCheckOrCarReadyStatusProps) => {
  const lastQualityCheckStatus = appointment.car_check_events?.findLast(ce =>
    appointment.car_checks?.some(check => ce.car_check_id === check.id && check.checklist?.checklist_type === CHECKLIST_TYPES.Quality)
  );

  if (isQualityCheck(appointment.appointment_status_identifier)) {
    const { name, timestamp } = getAppointmentStatusNameAndTime(appointment.appointment_status_identifier, appointment);

    return <StatusBarIcon status={appointment.appointment_status_identifier} name={name} timestamp={timestamp} active disabled />;
  }

  if (appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CarCheckStartedStatus && lastQualityCheckStatus) {
    return (
      <StatusBarIcon
        status={STATUS_IDENTIFIERS.QualityCheckStatus}
        name={lastQualityCheckStatus.user_full_name || ""}
        timestamp={lastQualityCheckStatus.created_on}
        active
        disabled
      />
    );
  }

  const { name, timestamp } = getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CarReadyStatus, appointment);

  if (isCarReady(appointment.appointment_status_identifier) || selectedLocation?.car_ready_btn_visible) {
    return (
      <StatusBarIcon
        name={name}
        timestamp={timestamp}
        status={STATUS_IDENTIFIERS.CarReadyStatus}
        active={isCarReady(appointment.appointment_status_identifier)}
        disabled
      />
    );
  }
};

export const AppointmentStatus = ({ appointment }: AppointmentStatusProps) => {
  const selectedLocation = useAppointmentLocation();
  const { checks } = useRealTimeOrderedChecks(appointment.id);

  const appointmentStatus: STATUS_IDENTIFIERS | undefined = appointment.appointment_status_identifier;
  const carInShopStatus = appointment.car_in_shop !== null && appointment.car_out_of_shop === null;

  const isActive = (statusId: number, appointmentStatusId?: STATUS_IDENTIFIERS) => appointmentStatusId === statusId;

  const carInShopName = () => {
    if (carInShopStatus) return appointment.car_in_shop_set_by ? `${appointment.car_in_shop_set_by.first_name} ${appointment.car_in_shop_set_by.last_name}`.trim() : "";
    else return (appointment.car_out_of_shop_set_by && `${appointment.car_out_of_shop_set_by.first_name} ${appointment.car_out_of_shop_set_by.last_name}`.trim()) || "";
  };

  return (
    <div className="AppointmentStatus">
      <StatusBarIcon
        name={carInShopName()}
        timestamp={carInShopStatus ? appointment.car_in_shop : appointment.car_out_of_shop}
        status={carInShopStatus ? STATUS_IDENTIFIERS.CarInShop : STATUS_IDENTIFIERS.CarOutOfShop}
        active={carInShopStatus}
        disabled
      />

      {getIntermittentStatus(appointment, checks)}

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CalculatingStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CalculatingStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.CalculatingStatus}
        disabled
        active={isActive(STATUS_IDENTIFIERS.CalculatingStatus, appointmentStatus)}
      />

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.PricingOKStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.PricingOKStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.PricingOKStatus}
        disabled
        active={isActive(STATUS_IDENTIFIERS.PricingOKStatus, appointmentStatus)}
      />

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.ContactStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.ContactStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.ContactStatus}
        customcomStatus={appointment.customer_communication?.status}
        disabled
        active={isActive(STATUS_IDENTIFIERS.ContactStatus, appointmentStatus)}
      />

      {selectedLocation?.diagnose_overview_enabled && (
        <StatusBarIcon
          status={STATUS_IDENTIFIERS.CustomerAnsweredStatus}
          name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerAnsweredStatus, appointment).name}
          timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerAnsweredStatus, appointment).timestamp}
          disabled
          active={appointmentStatus === STATUS_IDENTIFIERS.CustomerAnsweredStatus}
        />
      )}

      {appointmentStatus === STATUS_IDENTIFIERS.BackOrder && (
        <StatusBarIcon
          name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.BackOrder, appointment).name}
          timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.BackOrder, appointment).timestamp}
          status={STATUS_IDENTIFIERS.BackOrder}
          active
          disabled
        />
      )}

      <StatusBarIcon
        name={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerOKStatus, appointment).name}
        timestamp={getAppointmentStatusNameAndTime(STATUS_IDENTIFIERS.CustomerOKStatus, appointment).timestamp}
        status={STATUS_IDENTIFIERS.CustomerOKStatus}
        active={isActive(STATUS_IDENTIFIERS.CustomerOKStatus, appointmentStatus)}
        disabled
      />

      <QualityCheckOrCarReadyStatus appointment={appointment} selectedLocation={selectedLocation} />

      <div className="horizontal-line" />
    </div>
  );
};
